'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetPositionsRow, useGetPositions } from '@morfi/backend-sdk-v2/client';
import { AnimatePresence, domMax, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { PositionsEmpty } from '@/components/portfolio/empty-states/positions-empty';
import { isOwnViewer, usePortfolioContext } from '@/components/portfolio/portfolio-context';
import { PortfolioPanelHeader } from '@/components/portfolio/shared/portfolio-panel-header';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/app-tabs';
import { InfiniteList } from '@/components/ui/infinite-list';
import { Panel, PanelContent } from '@/components/ui/panel';
import { USDC_DUST_THRESHOLD_D6 } from '@/config/solana';
import { onEvent } from '@/events';
import type { UserPortfolioPositionDisplayRow } from '@/hooks/positions/user-portfolio/display-types';
import { useUserPortfolioSnapshotFetchedThisSession } from '@/hooks/positions/user-portfolio/query-select';
import { useUserPortfolioPositionRows } from '@/hooks/positions/user-portfolio/selectors';
import { hasPositiveSnapshotOutcomeBalance } from '@/lib/market/position-visibility';
import { POSITION_FIELDS } from '@/lib/sdk/projections';
import { logger } from '@/lib/telemetry/logger';
import { DURATION_POSITION_SMALL_MS, EASE_EXIT_MOTION, MOTION_POSITION_SMALL } from '@/lib/ui/easing';
import { evaluatePortfolioPositionVisibility } from './position-lifecycle-visibility';
import { PositionRow } from './position-row';
type ActiveTab = 'open' | 'closed';
type PanelPositionRow = {
  readonly kind: 'open';
  readonly row: UserPortfolioPositionDisplayRow;
} | {
  readonly kind: 'closed';
  readonly row: GetPositionsRow;
};
export function PortfolioPositionsPanel(t0) {
  const $ = _c(71);
  const {
    userId,
    snapshotUserId,
    walletPubkeys
  } = t0;
  const t = useTranslations("profile.stats");
  const t1 = usePortfolioContext();
  let t2;
  if ($[0] !== t1) {
    t2 = isOwnViewer(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isOwn = t2;
  const [activeTab, setActiveTab] = useState("open");
  const prefersReducedMotion = useReducedMotion();
  const warnedInvalidLifecycleRows = useRef(null);
  let t3;
  if ($[2] !== snapshotUserId || $[3] !== walletPubkeys) {
    t3 = {
      userId: snapshotUserId,
      wallets: walletPubkeys
    };
    $[2] = snapshotUserId;
    $[3] = walletPubkeys;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const openPositions = useUserPortfolioPositionRows(t3);
  const snapshotFetchedThisSession = useUserPortfolioSnapshotFetchedThisSession(snapshotUserId);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [...POSITION_FIELDS];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== userId || $[7] !== walletPubkeys) {
    t5 = {
      userId,
      walletPubkeys,
      status: "closed",
      includeZero: "true",
      fields: t4
    };
    $[6] = userId;
    $[7] = walletPubkeys;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = activeTab === "closed" && userId.length > 0;
  let t7;
  if ($[9] !== t6) {
    t7 = {
      enabled: t6
    };
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const closedPositions = useGetPositions(t5, t7);
  let t8;
  if ($[11] !== openPositions.rows) {
    t8 = openPositions.rows.filter(isOpenDisplayRow).toSorted(compareOpenRows).map(_temp);
    $[11] = openPositions.rows;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const openRows = t8;
  let t9;
  if ($[13] !== closedPositions.rows) {
    t9 = closedPositions.rows.filter(_temp2).toSorted(compareClosedRows).map(_temp3);
    $[13] = closedPositions.rows;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const closedRows = t9;
  const rows = activeTab === "open" ? openRows : closedRows;
  const isPending = activeTab === "open" ? openPositions.isPending : closedPositions.isPending;
  const isError = activeTab === "open" ? openPositions.isError : closedPositions.isError;
  let t10;
  if ($[15] !== prefersReducedMotion) {
    t10 = prefersReducedMotion === true ? {
      duration: 0
    } as const : MOTION_POSITION_SMALL;
    $[15] = prefersReducedMotion;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const layoutTransition = t10;
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      duration: DURATION_POSITION_SMALL_MS / 1000,
      ease: EASE_EXIT_MOTION
    };
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const presenceExitTransition = t11 as const;
  let t12;
  let t13;
  if ($[18] !== closedPositions.rows) {
    t12 = () => {
      let warningKeys = warnedInvalidLifecycleRows.current;
      if (warningKeys === null) {
        warningKeys = new Set();
        warnedInvalidLifecycleRows.current = warningKeys;
      }
      for (const row_2 of closedPositions.rows) {
        const {
          invalidFields
        } = evaluatePortfolioPositionVisibility(row_2);
        if (invalidFields.length === 0) {
          continue;
        }
        const warningKey = [row_2.market, String(row_2.outcomeIndex), invalidFields.join(",")].join("|");
        if (warningKeys.has(warningKey)) {
          continue;
        }
        warningKeys.add(warningKey);
        logger.warn("portfolio.position_lifecycle_invalid", {
          invalidFields,
          resultState: row_2.resultState
        });
      }
    };
    t13 = [closedPositions.rows];
    $[18] = closedPositions.rows;
    $[19] = t12;
    $[20] = t13;
  } else {
    t12 = $[19];
    t13 = $[20];
  }
  useEffect(t12, t13);
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = function handleTabChange(next) {
      if (next !== "open" && next !== "closed") {
        return;
      }
      setActiveTab(next);
      onEvent("portfolio_positions_toggled", {
        to: next
      });
    };
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const handleTabChange = t14;
  let t15;
  if ($[22] !== t) {
    t15 = t("positions_section_title");
    $[22] = t;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t) {
    t16 = t("open_tab");
    $[24] = t;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t16) {
    t17 = <TabsTrigger value="open">{t16}</TabsTrigger>;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t) {
    t18 = t("closed_tab");
    $[28] = t;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t18) {
    t19 = <TabsTrigger value="closed">{t18}</TabsTrigger>;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== t17 || $[33] !== t19) {
    t20 = <TabsList variant="segmented" className="w-auto">{t17}{t19}</TabsList>;
    $[32] = t17;
    $[33] = t19;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== activeTab || $[36] !== t20) {
    t21 = <div className="flex items-center gap-2"><Tabs value={activeTab} onValueChange={handleTabChange} className="shrink-0">{t20}</Tabs></div>;
    $[35] = activeTab;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t15 || $[39] !== t21) {
    t22 = <PortfolioPanelHeader title={t15} action={t21} />;
    $[38] = t15;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== prefersReducedMotion) {
    t23 = prefersReducedMotion === true ? false : {
      opacity: 0,
      y: 8
    };
    $[41] = prefersReducedMotion;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      opacity: 1,
      y: 0
    };
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  let t26;
  if ($[44] !== prefersReducedMotion) {
    t25 = prefersReducedMotion === true ? {
      opacity: 1
    } : {
      opacity: 0,
      y: -6,
      transition: presenceExitTransition
    };
    t26 = prefersReducedMotion === true ? {
      duration: 0
    } : MOTION_POSITION_SMALL;
    $[44] = prefersReducedMotion;
    $[45] = t25;
    $[46] = t26;
  } else {
    t25 = $[45];
    t26 = $[46];
  }
  let t27;
  if ($[47] !== isOwn || $[48] !== snapshotFetchedThisSession) {
    t27 = item_0 => item_0.kind === "open" ? <PositionRow row={item_0.row} mode="open" isOwnPortfolio={isOwn} claimReady={snapshotFetchedThisSession} /> : <PositionRow row={item_0.row} mode="closed" isOwnPortfolio={isOwn} />;
    $[47] = isOwn;
    $[48] = snapshotFetchedThisSession;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div className="flex flex-col gap-2" data-testid="positions-loading-skeletons" aria-busy="true"><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></div>;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== activeTab) {
    t29 = <PositionsEmpty tab={activeTab} />;
    $[51] = activeTab;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== isError || $[54] !== isPending || $[55] !== rows || $[56] !== t27 || $[57] !== t29) {
    t30 = <InfiniteList items={rows} getItemKey={_temp4} renderItem={t27} hasNextPage={false} isFetchingNextPage={false} fetchNextPage={noNextPage} isError={isError} isInitialLoading={isPending} loadingPlaceholder={t28} emptyState={t29} listClassName="flex flex-col gap-2.5" />;
    $[53] = isError;
    $[54] = isPending;
    $[55] = rows;
    $[56] = t27;
    $[57] = t29;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== activeTab || $[60] !== t23 || $[61] !== t25 || $[62] !== t26 || $[63] !== t30) {
    t31 = <AnimatePresence mode="wait" initial={false}><m.div key={activeTab} layout={false} className="flex w-full min-w-0 flex-col" initial={t23} animate={t24} exit={t25} transition={t26}>{t30}</m.div></AnimatePresence>;
    $[59] = activeTab;
    $[60] = t23;
    $[61] = t25;
    $[62] = t26;
    $[63] = t30;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== layoutTransition || $[66] !== t31) {
    t32 = <LazyMotion features={domMax}><m.div layout={true} transition={layoutTransition} className="min-w-0">{t31}</m.div></LazyMotion>;
    $[65] = layoutTransition;
    $[66] = t31;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== t22 || $[69] !== t32) {
    t33 = <Panel><PanelContent className="flex flex-col gap-4">{t22}{t32}</PanelContent></Panel>;
    $[68] = t22;
    $[69] = t32;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  return t33;
}
function _temp4(item) {
  return item.row.positionId;
}
function _temp3(row_1) {
  return {
    kind: "closed",
    row: row_1
  };
}
function _temp2(row_0) {
  return evaluatePortfolioPositionVisibility(row_0).isVisible;
}
function _temp(row) {
  return {
    kind: "open",
    row
  };
}
function isOpenDisplayRow(row: UserPortfolioPositionDisplayRow): boolean {
  if (!hasPositiveSnapshotOutcomeBalance(row)) return false;
  const currentValueD6 = row.displayMark.currentValueD6;
  return currentValueD6 === null || currentValueD6 >= BigInt(USDC_DUST_THRESHOLD_D6);
}
function compareOpenRows(left: UserPortfolioPositionDisplayRow, right: UserPortfolioPositionDisplayRow): number {
  const leftValue = left.displayMark.currentValueD6;
  const rightValue = right.displayMark.currentValueD6;
  if (leftValue === null) return 1;
  if (rightValue === null) return -1;
  if (leftValue === rightValue) return left.positionId.localeCompare(right.positionId);
  return leftValue > rightValue ? -1 : 1;
}
function compareClosedRows(left: GetPositionsRow, right: GetPositionsRow): number {
  const leftValue = signedWire(left.realizedPnlD6);
  const rightValue = signedWire(right.realizedPnlD6);
  if (leftValue === null) return 1;
  if (rightValue === null) return -1;
  if (leftValue === rightValue) return left.positionId.localeCompare(right.positionId);
  return leftValue > rightValue ? -1 : 1;
}
function signedWire(value: string): bigint | null {
  return /^-?(?:0|[1-9]\d*)$/.test(value) ? BigInt(value) : null;
}
function noNextPage(): void {}